import { FunctionComponent, useCallback, useState } from "react";

import { useTranslation } from "react-i18next";

import {
	faChevronDown,
	faChevronUp,
	faTriangleExclamation,
} from "@fortawesome/pro-regular-svg-icons";

import { TextField } from "@mui/material";

import { Button } from "@/components/Button/Button";
import { ButtonColor } from "@/components/Button/types";
import { AutoCompleteItem, Dropdown } from "@/components/Dropdown/Dropdown";
import { Icon } from "@/components/Icon/Icon";
import { Modal } from "@/components";

import { useResetListMutation } from "@/pages/Private/redux/list/list.api";

import { classNames } from "@/utils/classNames";

import { List } from "../../schema/list";
import { TitleScreeningGate } from "../../../CsvEnrichment/components/titleGate/TitleScreeningGate";
import { XlsStatistics } from "../../../CsvEnrichment/components/titleGate/XlsStatistics";
import { useFieldMapping } from "./useFieldMapping";

export interface FieldMappingCardProps {
	list: List;
}

/**
 * "Field Mapping": maps the columns of the provided file onto the system fields that get enriched.
 */
export const FieldMappingCard: FunctionComponent<FieldMappingCardProps> = ({ list }) => {
	const { t } = useTranslation();
	const ts = useCallback((key: string) => t(`list.${key}`), [t]);

	const [resetList, { isLoading: resetListIsLoading }] = useResetListMutation();
	const [showResetModal, setShowResetModal] = useState<boolean>(false);
	const [fileMapping, setFileMapping] = useState<boolean>(true);

	const {
		allowUpload,
		alreadyImported,
		defaultOptions,
		foundFields,
		isSaving,
		isSuggesting,
		newColumns,
		options,
		saveMapping,
		setFoundFields,
		targetFileName,
		gateVisible,
		statsVisible,
		preview,
		companyPreview,
		rowStats,
		screenTitles,
		setScreenTitles,
	} = useFieldMapping(list);

	const tr = useCallback(
		(key: string, options?: { count?: number }) =>
			t(`customEvent.rescrape.mapping.${key}`, options),
		[t]
	);

	const handleResetList = async () => {
		try {
			await resetList(list.id).unwrap();
			setShowResetModal(false);
		} catch (err) {
			console.error(err);
		}
	};

	return (
		<>
			<div
				className={classNames(
					"border p-6 w-full items-start relative rounded-xl border-1 border-border-light group",
					"flex flex-col text-left gap-6",
					fileMapping ? "bg-background-light" : "bg-white"
				)}
			>
				<button
					className={classNames(
						"w-full items-center group cursor-pointer flex flex-row text-left gap-6"
					)}
					onClick={() => {
						setFileMapping(!fileMapping);
					}}
				>
					<div className="grow">
						<div className="text-smd text-text font-bold">Field Mapping</div>
						<div className="text-ssm text-text-meta">
							{targetFileName ? tr("subtitleForFile") : ""}
							{targetFileName
								? ` ${targetFileName}`
								: "Map the fields to the attributes you want to enrich."}
						</div>
					</div>
					<div
						className={classNames(
							"flex min-w-[36px]  !w-[36px] !h-[36px] group-hover:bg-blue-action rounded-md items-center justify-center border",
							fileMapping
								? "border-border-light bg-white"
								: "border-transparent bg-background-light"
						)}
					>
						<Icon
							className="text-text-meta group-hover:text-white w-[12px] h-auto"
							icon={fileMapping ? faChevronUp : faChevronDown}
						/>
					</div>
				</button>
				{fileMapping && (
					<div className={classNames("flex flex-col w-full mb-4  transition-opacity duration-300")}>
						{alreadyImported && (
							<div className="absolute flex flex-col z-10 top-8  bg-white rounded-xl left-1/2 translate-x-[-50%] flex flex-row gap-4 w-[calc(100%-2rem)] shadow-sm border border-border-light p-4">
								<div className="text-sm text-text font-bold">
									You have already imported this file. You can only map the fields once. If you want
									to map the fields again, you need to reset the list.
								</div>
								<div className="w-fit">
									<Button
										color={ButtonColor.ACTION}
										title="Reset list"
										onClick={() => setShowResetModal(true)}
									/>
								</div>
							</div>
						)}
						{newColumns.size > 0 && (
							<div className="mb-4 flex flex-row items-start gap-2 rounded-xl border border-status-warning-light bg-status-running-light px-4 py-3 text-xs text-status-running">
								<Icon className="w-[14px] h-auto shrink-0 mt-0.5" icon={faTriangleExclamation} />
								<span>{tr("newColumnsWarning", { count: newColumns.size })}</span>
							</div>
						)}
						{isSuggesting && <div className="mb-4 text-xs text-text-meta">{tr("suggesting")}</div>}
						<div
							className={classNames(
								"w-full max-w-[500px]",
								alreadyImported ? "group-hover:opacity-50 cursor-not-allowed" : ""
							)}
						>
							<ul>
								{foundFields
									?.filter((a) => a.foundField)
									?.map((item, index) => {
										const selectedField = defaultOptions.find((o) => {
											return o.id === item.assignedField;
										});

										const isNewColumn = newColumns.has(item.foundField);

										return (
											<li
												key={`list-index-${index}`}
												className={classNames(
													"text-xs flex flex-row mb-2 justify-between items-center gap-2",
													isNewColumn ? "rounded-lg bg-status-running-light px-2 py-1" : ""
												)}
											>
												<div className="flex w-[150px] flex-row items-center gap-1">
													<span className="text-sm">{item.foundField}</span>
													{isNewColumn && (
														<span className="rounded-lg border border-status-warning-light bg-white px-1.5 py-0.5 text-[10px] font-semibold text-status-running">
															{tr("newBadge")}
														</span>
													)}
												</div>
												<div className="min-w-[250px] grow flex self-stretch">
													<Dropdown
														classNameButton={classNames(
															selectedField?.id === "" ? "text-gray-200" : ""
														)}
														data={options}
														defaultValue={selectedField || { title: "Please select", id: "" }}
														disabled={alreadyImported}
														floating={true}
														handleSelect={(value?: AutoCompleteItem) => {
															if (value) {
																setFoundFields([
																	...foundFields.slice(0, index),
																	{
																		foundField: item.foundField.trim(),
																		assignedField: value?.id?.toString()?.trim() || "",
																	},
																	...foundFields.slice(index + 1),
																]);
															}
														}}
													/>
												</div>
											</li>
										);
									})}
							</ul>
						</div>
						{/* The screening toggle belongs with Save mapping: that button is what starts the
						    import it changes the cost of (PREC-3536). */}
						{gateVisible && (
							<TitleScreeningGate
								isChecked={screenTitles}
								preview={preview}
								onChange={() => setScreenTitles(!screenTitles)}
							/>
						)}

						{statsVisible && (
							<XlsStatistics
								companyPreview={companyPreview}
								entityType={list.entityType}
								preview={preview}
								rowStats={rowStats}
							/>
						)}

						<div className="flex flex-row gap-4 justify-end">
							<div className="w-auto mt-4">
								<Button
									color={ButtonColor.ACTION}
									disabled={!allowUpload || alreadyImported}
									isLoading={isSaving}
									title="Save mapping"
									onClick={saveMapping}
								/>
							</div>
						</div>
					</div>
				)}
			</div>
			{showResetModal && (
				<Modal
					handleClose={() => setShowResetModal(false)}
					handleSave={handleResetList}
					isLoading={resetListIsLoading}
					isOpened={showResetModal}
					submitButtonColor="error"
					submitButtonText={t("basics.reset")}
					title={ts("reset.title")}
				>
					<div className="text-sm text-gray-700 mb-4">{ts("reset.confirmation")}</div>
					<TextField
						fullWidth
						defaultValue={list.name}
						inputProps={{ readOnly: true }}
						label={ts("name")}
					/>
				</Modal>
			)}
		</>
	);
};
